import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    TextField,
    MenuItem,
    Button,
    Typography,
    Box,
    Grid,
    Paper,
    FormControl,
    InputLabel,
    OutlinedInput,
    FormHelperText,
    Checkbox,
    ListItemText,
    Chip,
    Avatar,
} from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import Layout from '../Layout';
import api from '../api';
import { toast } from 'react-toastify';
import { DEPARTMENT_OPTIONS } from '../constants/departments';

const roles = ['Admin', 'Senior Manager', 'Owner', 'Manager'];

const ALL_LOCATIONS_VALUE = '__ALL_LOCATIONS__';
const ALL_LAWAREAS_VALUE = '__ALL_LAWAREAS__';
const ALL_DEPARTMENTS_VALUE = '__ALL_DEPARTMENTS__';

// Light SaaS-like theme 
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#0F6FFF' },
        secondary: { main: '#059669' },
        background: {
            default: '#F3F6FD',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#0F172A',
            secondary: '#64748B',
        },
    },
    shape: {
        borderRadius: 16,
    },
    typography: {
        fontFamily:
            "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        h4: {
            fontWeight: 700,
            letterSpacing: 0.2,
        },
        subtitle2: {
            fontWeight: 500,
            textTransform: 'uppercase',
            fontSize: 11,
            letterSpacing: 1,
            color: '#9CA3AF',
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 9999,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': {
                        borderColor: '#E5E7EB',
                    },
                    '&:hover fieldset': {
                        borderColor: '#C7D2FE',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#0F6FFF',
                    },
                },
                input: {
                    paddingTop: 10,
                    paddingBottom: 10,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 9999,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 10px 25px rgba(15,118,255,0.25)',
                },
            },
        },
    },
});

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1.5),
    },
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2.5),
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    background: 'rgba(255,255,255,0.96)',
    boxShadow: '0 18px 40px rgba(15,23,42,0.10)',
    border: '1px solid rgba(148,163,184,0.25)',
    backdropFilter: 'blur(18px)',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 24,
    background: 'rgba(255,255,255,0.96)',
    boxShadow: '0 22px 55px rgba(15,23,42,0.10)',
    border: '1px solid rgba(148,163,184,0.18)',
    backdropFilter: 'blur(18px)',
}));

export default function UserForm() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        role: '',
        company: '',
        location: [],
        lawarea: [],
        departments: [],
    });

    const [companies, setCompanies] = useState([]);
    const [locations, setLocations] = useState([]);
    const [lawAreas, setLawAreas] = useState([]);

    // flatten locations once
    const flatLocations = useMemo(
        () => (Array.isArray(locations) ? locations.flat() : []),
        [locations]
    );

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'location') {
            const values = typeof value === 'string' ? value.split(',') : value;

            // handle "All Locations"
            if (values.includes(ALL_LOCATIONS_VALUE)) {
                setFormData((prev) => ({
                    ...prev,
                    location:
                        prev.location.length === flatLocations.length ? [] : flatLocations,
                }));
            } else {
                setFormData((prev) => ({ ...prev, location: values }));
            }
            return;
        }

        if (name === 'lawarea') {
            const values = typeof value === 'string' ? value.split(',') : value;

            // handle "All Law Areas"
            if (values.includes(ALL_LAWAREAS_VALUE)) {
                setFormData((prev) => ({
                    ...prev,
                    lawarea:
                        prev.lawarea.length === lawAreas.length ? [] : lawAreas,
                }));
            } else {
                setFormData((prev) => ({ ...prev, lawarea: values }));
            }
            return;
        }

        if (name === 'departments') {
            const values = typeof value === 'string' ? value.split(',') : value;
            if (values.includes(ALL_DEPARTMENTS_VALUE)) {
                setFormData((prev) => ({
                    ...prev,
                    departments:
                        prev.departments.length === DEPARTMENT_OPTIONS.length
                            ? []
                            : DEPARTMENT_OPTIONS,
                }));
            } else {
                setFormData((prev) => ({ ...prev, departments: values }));
            }
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await api.post('/auth/add-user', formData);
            toast.success(
                'User created successfully! A welcome email has been sent to the user.'
            );

            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                mobile: '',
                role: '',
                company: '',
                location: [],
                lawarea: [],
                departments: [],
            });
        } catch (error) {
            console.error('Error:', error);

            // No response at all (network/server down, CORS, etc.)
            if (!error.response) {
                toast.error('Network error. Please check your connection and try again.');
                return;
            }

            const { data, status } = error.response || {};
            const { error: errorCode, message, details } = data || {};

            let finalMessage = '';

            // 1. If validation error, show the first field error (or join all)
            if (errorCode === 'VALIDATION_ERROR' && details && typeof details === 'object') {
                const fieldErrors = Object.values(details);
                if (fieldErrors.length > 0) {
                    // Option A: show only the first error
                    finalMessage = fieldErrors[0];
                    // Option B: show all errors joined
                    // finalMessage = fieldErrors.join('\n');
                }
            }

            // 2. If backend sent a specific message, use it
            if (!finalMessage && message) {
                finalMessage = message;
            }

            // 3. Fallback based on errorCode if still nothing
            if (!finalMessage && errorCode) {
                switch (errorCode) {
                    case 'USER_ALREADY_EXISTS':
                        finalMessage = 'A user with this email already exists.';
                        break;
                    case 'COMPANY_NOT_FOUND':
                        finalMessage = 'The specified company was not found.';
                        break;
                    case 'EMAIL_SENDING_FAILED':
                        finalMessage =
                            'User created, but we were unable to send the welcome email.';
                        break;
                    default:
                        finalMessage = 'An error occurred while creating the user.';
                }
            }

            // 4. Last generic fallback
            if (!finalMessage) {
                finalMessage =
                    status >= 500
                        ? 'Server error. Please try again later.'
                        : 'An error occurred while creating the user.';
            }

            toast.error(finalMessage);
        }
    };


    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const { data } = await api.get(`detail/userinfo?view=meta`);
                const companyNames = Array.isArray(data.companyDetails?.company_name)
                    ? data.companyDetails.company_name
                    : data.companyDetails?.company_name
                        ? [data.companyDetails.company_name]
                        : [];

                setCompanies(companyNames);
                setLocations(data.companyDetails?.locations || []);
            } catch (error) {
                console.error('Error fetching companies data:', error);
            }
        };

        const fetchLawAreas = async () => {
            try {
                const { data } = await api.get(
                    `tracker/law-area`
                );
                const lawAreaArray = data.lawareas?.map((area) => area.law_area) || [];
                setLawAreas(lawAreaArray);
            } catch (error) {
                console.error('Error fetching law areas data:', error);
            }
        };

        fetchCompanies();
        fetchLawAreas();
    }, []);

    const allLocationsSelected =
        flatLocations.length > 0 &&
        formData.location.length === flatLocations.length;

    const allLawAreasSelected =
        lawAreas.length > 0 && formData.lawarea.length === lawAreas.length;
    const allDepartmentsSelected =
        DEPARTMENT_OPTIONS.length > 0 &&
        formData.departments.length === DEPARTMENT_OPTIONS.length;

    return (
        <Layout title="Add User">
            <ThemeProvider theme={theme}>
                <Box
                    sx={{
                        minHeight: '100vh',
                        bgcolor: 'background.default',
                        backgroundImage:
                            'linear-gradient(to bottom, #E5EDFF 0%, #F3F6FD 28%, #F9FBFF 100%)',
                        py: 4,
                    }}
                >
                    <StyledContainer maxWidth="md">
                        {/* Header card similar to "Manage Users" */}
                        <HeaderCard elevation={0}>
                            <Avatar
                                sx={{
                                    bgcolor: 'primary.main',
                                    width: 40,
                                    height: 40,
                                    boxShadow: '0 8px 20px rgba(15,118,255,0.35)',
                                }}
                            >
                                <PersonAddAlt1Icon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">User Management</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Add New User
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    Enter the details below to create a new user in your
                                    workspace.
                                </Typography>
                            </Box>
                        </HeaderCard>

                        {/* Form card */}
                        <StyledPaper elevation={0}>
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                {/* First / Last name */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" size="small">
                                            <InputLabel>First Name</InputLabel>
                                            <OutlinedInput
                                                label="First Name"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                            />
                                            <FormHelperText>
                                                Enter the first name of the user.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" size="small">
                                            <InputLabel>Last Name</InputLabel>
                                            <OutlinedInput
                                                label="Last Name"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                            />
                                            <FormHelperText>
                                                Enter the last name of the user.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Email / Mobile */}
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" size="small">
                                            <InputLabel>E-mail</InputLabel>
                                            <OutlinedInput
                                                label="E-mail"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                            <FormHelperText>
                                                Enter the e-mail address of the user.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" size="small">
                                            <InputLabel>Mobile</InputLabel>
                                            <OutlinedInput
                                                label="Mobile"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                            />
                                            <FormHelperText>
                                                Enter the mobile number of the user.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <TextField
                                                select
                                                name="departments"
                                                label="Departments (Optional)"
                                                value={formData.departments}
                                                onChange={handleChange}
                                                variant="outlined"
                                                size="small"
                                                SelectProps={{
                                                    multiple: true,
                                                    renderValue: (selected) => {
                                                        if (
                                                            DEPARTMENT_OPTIONS.length > 0 &&
                                                            selected.length === DEPARTMENT_OPTIONS.length
                                                        ) {
                                                            return 'All Departments';
                                                        }
                                                        return selected.join(', ');
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    shrink: formData.departments.length > 0,
                                                }}
                                            >
                                                <MenuItem value={ALL_DEPARTMENTS_VALUE}>
                                                    <Checkbox
                                                        size="small"
                                                        indeterminate={
                                                            formData.departments.length > 0 &&
                                                            formData.departments.length < DEPARTMENT_OPTIONS.length
                                                        }
                                                        checked={allDepartmentsSelected}
                                                    />
                                                    <ListItemText primary="All Departments" />
                                                </MenuItem>

                                                {DEPARTMENT_OPTIONS.map((dep) => (
                                                    <MenuItem key={dep} value={dep}>
                                                        <Checkbox
                                                            checked={formData.departments.includes(dep)}
                                                            size="small"
                                                        />
                                                        <ListItemText primary={dep} />
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <FormHelperText sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                                Optional: select one or more departments.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Role / Company */}
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            size="small"
                                            name="role"
                                            label="Role Name"
                                            value={formData.role}
                                            onChange={handleChange}
                                            fullWidth
                                        >
                                            {roles.map((role) => (
                                                <MenuItem key={role} value={role.toLowerCase()}>
                                                    {role}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            size="small"
                                            name="company"
                                            label="Company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            fullWidth
                                        >
                                            {companies.map((company) => (
                                                <MenuItem key={company} value={company}>
                                                    {company}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                {/* Location / Law Area */}
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    {/* Location with "All" */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            size="small"
                                            name="location"
                                            label="Location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            fullWidth
                                            disabled={!formData.company}
                                            variant="outlined"
                                            SelectProps={{
                                                multiple: true,
                                                renderValue: (selected) => {
                                                    if (
                                                        flatLocations.length > 0 &&
                                                        selected.length === flatLocations.length
                                                    ) {
                                                        return 'All Locations';
                                                    }
                                                    return selected.join(', ');
                                                },
                                            }}
                                        >
                                            <MenuItem value={ALL_LOCATIONS_VALUE}>
                                                <Checkbox
                                                    size="small"
                                                    indeterminate={
                                                        formData.location.length > 0 &&
                                                        formData.location.length < flatLocations.length
                                                    }
                                                    checked={allLocationsSelected}
                                                />
                                                <ListItemText primary="All Locations" />
                                            </MenuItem>

                                            {flatLocations.map((loc) => (
                                                <MenuItem key={loc} value={loc}>
                                                    <Checkbox
                                                        checked={formData.location.includes(loc)}
                                                        size="small"
                                                    />
                                                    <ListItemText primary={loc} />
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <FormHelperText sx={{ mt: 0.5 }}>
                                            Select one or more locations.
                                        </FormHelperText>
                                    </Grid>

                                    {/* Law Area with "All" */}
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <TextField
                                                select
                                                name="lawarea"
                                                label="Law Area"
                                                value={formData.lawarea}
                                                onChange={handleChange}
                                                variant="outlined"
                                                size="small"
                                                SelectProps={{
                                                    multiple: true,
                                                    renderValue: (selected) => {
                                                        if (
                                                            lawAreas.length > 0 &&
                                                            selected.length === lawAreas.length
                                                        ) {
                                                            return (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 0.3,
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        label="All Law Areas"
                                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                                    />
                                                                </Box>
                                                            );
                                                        }

                                                        return (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 0.3,
                                                                }}
                                                            >
                                                                {selected.map((value) => (
                                                                    <Chip
                                                                        key={value}
                                                                        label={value}
                                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        );
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    shrink: formData.lawarea.length > 0,
                                                }}
                                            >
                                                <MenuItem value={ALL_LAWAREAS_VALUE}>
                                                    <Checkbox
                                                        size="small"
                                                        indeterminate={
                                                            formData.lawarea.length > 0 &&
                                                            formData.lawarea.length < lawAreas.length
                                                        }
                                                        checked={allLawAreasSelected}
                                                    />
                                                    <ListItemText primary="All Law Areas" />
                                                </MenuItem>

                                                {lawAreas.map((area) => (
                                                    <MenuItem key={area} value={area}>
                                                        <Checkbox
                                                            checked={formData.lawarea.includes(area)}
                                                            size="small"
                                                        />
                                                        <ListItemText
                                                            primary={area}
                                                            sx={{ fontSize: '0.85rem' }}
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <FormHelperText sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                                Select one or more Law Areas.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Submit button */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 3 }}
                                    fullWidth
                                >
                                    Add User
                                </Button>
                            </Box>
                        </StyledPaper>
                    </StyledContainer>
                </Box>
            </ThemeProvider>
        </Layout>
    );
}
